import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';

const ColumnMappingDialog = ({ 
  open, 
  onClose, 
  onConfirm, 
  headers, 
  previewData,
  // Optional: content shown above the preview (e.g. the "which WhatsApp?" picker) and a way to hold the import button.
  extraContent = null,
  confirmDisabled = false,
}) => {
  const [detectedColumns, setDetectedColumns] = useState({ nameColumn: '', phoneColumn: '' });
  const [detectionStatus, setDetectionStatus] = useState('detecting');

  // Enhanced column detection function
  const detectColumns = (headers, previewData) => {
    if (!headers || !previewData || previewData.length === 0) {
      return { nameColumn: '', phoneColumn: '' };
    }

    // Score each column based on content analysis
    const columnScores = headers.map((header, index) => {
      const columnData = previewData.map(row => row[header]).filter(val => val && String(val).trim() !== '');
      const sampleValues = columnData.slice(0, 10); // Analyze first 10 non-empty values
      
      let nameScore = 0;
      let phoneScore = 0;
      
      // Name detection patterns
      const namePatterns = ['name', 'contact_name', 'full_name', 'first_name', 'contact', 'customer'];
      const headerLower = header.toLowerCase();
      
      // Check header name
      if (namePatterns.some(pattern => headerLower.includes(pattern))) {
        nameScore += 10;
      }
      
      // Check content patterns for names
      sampleValues.forEach(value => {
        const strValue = String(value).trim();
        
        // Name patterns: contains letters, spaces, common name indicators
        if (/^[a-zA-Z\s.\-']+$/.test(strValue) && strValue.length > 2 && strValue.length < 50) {
          nameScore += 2;
        }
        
        // Check for common name indicators
        if (strValue.includes(' ') || strValue.includes('.') || strValue.includes('-')) {
          nameScore += 1;
        }
      });
      
      // Phone detection patterns
      const phonePatterns = ['phone', 'phone_number', 'mobile', 'contact_number', 'number', 'phoneNumberID', 'tel'];
      
      // Check header name
      if (phonePatterns.some(pattern => headerLower.includes(pattern))) {
        phoneScore += 10;
      }
      
      // Check content patterns for phone numbers
      sampleValues.forEach(value => {
        const strValue = String(value).trim();
        
        // Phone patterns: contains digits, +, -, (, ), spaces
        if (/^[+\d\s\-().]+$/.test(strValue) && strValue.length >= 7 && strValue.length <= 20) {
          phoneScore += 3;
        }
        
        // Check for phone number indicators
        if (strValue.startsWith('+') || strValue.includes('(') || strValue.includes(')')) {
          phoneScore += 2;
        }
        
        // Check if it's mostly digits
        const digitCount = (strValue.match(/\d/g) || []).length;
        if (digitCount >= 7) {
          phoneScore += 2;
        }
      });
      
      return {
        header,
        index,
        nameScore,
        phoneScore,
        sampleValues: sampleValues.slice(0, 3) // Keep first 3 samples for debugging
      };
    });
    
    // Find best name and phone columns
    const bestNameColumn = columnScores.reduce((best, current) => 
      current.nameScore > best.nameScore ? current : best
    );
    
    const bestPhoneColumn = columnScores.reduce((best, current) => 
      current.phoneScore > best.phoneScore ? current : best
    );
    
    return {
      nameColumn: bestNameColumn.nameScore > 0 ? bestNameColumn.header : '',
      phoneColumn: bestPhoneColumn.phoneScore > 0 ? bestPhoneColumn.header : '',
      scores: columnScores
    };
  };

  useEffect(() => {
    if (open && headers && previewData) {
      setDetectionStatus('detecting');
      
      // Small delay to show detection status
      setTimeout(() => {
        const detection = detectColumns(headers, previewData);
        setDetectedColumns(detection);
        
        if (detection.nameColumn && detection.phoneColumn) {
          setDetectionStatus('success');
        } else {
          setDetectionStatus('failed');
        }
      }, 500);
    }
  }, [open, headers, previewData]);

  const handleConfirm = () => {
    if (detectedColumns.nameColumn && detectedColumns.phoneColumn) {
      onConfirm(detectedColumns.nameColumn, detectedColumns.phoneColumn);
    }
  };

  const handleClose = () => {
    setDetectedColumns({ nameColumn: '', phoneColumn: '' });
    setDetectionStatus('detecting');
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          backgroundColor: (theme) => theme.palette.background.paper,
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h6" component="div" sx={{ fontSize: 22, fontWeight: 600 }}>
          Import Contacts
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: 15 }}>
          {detectionStatus === 'detecting' && 'Analyzing file structure...'}
          {detectionStatus === 'success' && 'Ready to import contacts'}
          {detectionStatus === 'failed' && 'Could not detect name and phone columns'}
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {extraContent && detectionStatus === 'success' && <Box sx={{ mb: 2 }}>{extraContent}</Box>}
        {detectionStatus === 'detecting' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: 16 }}>
              Analyzing file structure to detect name and phone number columns...
            </Typography>
          </Box>
        )}

        {detectionStatus === 'success' && (
          <Box>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: 18, fontWeight: 600 }}>
                Detected Columns
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Chip 
                  label={`Name: ${detectedColumns.nameColumn}`} 
                  color="primary" 
                  variant="outlined"
                  sx={{ fontSize: 15, height: 36 }}
                />
                <Chip 
                  label={`Phone: ${detectedColumns.phoneColumn}`} 
                  color="secondary" 
                  variant="outlined"
                  sx={{ fontSize: 15, height: 36 }}
                />
              </Box>
            </Box>

            {previewData && previewData.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontSize: 18, fontWeight: 600 }}>
                  Preview (First 5 rows)
                </Typography>
                <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                  <Table stickyHeader size="small" sx={{ "& .MuiTableCell-root": { fontSize: 15, py: 1.25 } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Phone Number</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewData.slice(0, 5).map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            {detectedColumns.nameColumn ? row[detectedColumns.nameColumn] || '-' : '-'}
                          </TableCell>
                          <TableCell>
                            {detectedColumns.phoneColumn ? row[detectedColumns.phoneColumn] || '-' : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        )}

        {detectionStatus === 'failed' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="error" sx={{ mb: 2 }}>
              Could not automatically detect name and phone number columns in your file.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please ensure your file has clear column headers like "Name" and "Phone" or "Phone Number".
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained"
          disabled={detectionStatus !== 'success' || confirmDisabled}
        >
          Import Contacts
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ColumnMappingDialog;
